/**
 * manualControls.ts - Catalogue des controles "Gouvernance & Organisation"
 *
 * Ces controles couvrent les exigences NIS2 Art.21, CyFun (CCB) et ISO 27001:2022
 * Annexe A qui ne sont PAS auditables techniquement via Microsoft 365 (politiques
 * RH, securite physique, supply chain, formation, BCP/DR, etc.).
 *
 * Modele :
 *  - Tous les controles sont en mode manuel (`manual: true` toujours)
 *  - L auditeur saisit une decision via la page Compliance Manager
 *  - La decision est persistee via /api/clients/manual-decisions (memes endpoints
 *    que les manual checks M365 existants)
 *  - Le score consolide compte ces decisions au meme titre que les checks auto
 *
 * Famille : type `GovernanceFamily` distinct du type `Family` M365 pour eviter
 * toute collision avec le moteur de scan et la page Controls existante.
 */

import type { Severity, ControlReference } from './types';

/**
 * Domaines de contrôles gouvernance/organisation.
 * 10 domaines couvrant l ensemble des gaps NIS2/CyFun/ISO non couverts par M365.
 */
export type GovernanceFamily =
  | 'governance'    // Politiques, gouvernance, ISMS
  | 'hr'            // Personnel security, screening, exit process
  | 'physical'      // Securite physique et environnementale
  | 'network'       // Architecture reseau non-M365 (firewall, VPN, segmentation)
  | 'continuity'    // BCP / DR / Backup
  | 'supply'        // Supply chain et tiers
  | 'training'      // Formation et sensibilisation
  | 'crypto'        // Politique cryptographique
  | 'asset'         // Inventaire des actifs / classification
  | 'incident';     // Procedures de gestion d incident

/** Definition d un controle gouvernance/organisation (catalogue manuel). */
export interface GovernanceControlDefinition {
  /** Identifiant stable, prefixe par domaine : GOV-001, HR-001, BCP-001, etc. */
  id: string;
  family: GovernanceFamily;
  title: string;
  description: string;
  rationale: string;
  severity: Severity;
  references: ControlReference[];
  /** Conseils de mise en oeuvre pour passer le controle conforme. */
  remediation: string;
}

// ----------------------------------------------------------------------------
//                          CATALOGUE COMPLET (45 controles)
// ----------------------------------------------------------------------------
//
//  Mapping frameworks systematique :
//   - NIS2 Art. 21(2)(a-j) : 10 sous-clauses obligatoires
//   - CyFun (CCB) : 5 fonctions ID/PR/DE/RS/RC, niveaux Basic/Important/Essential
//   - ISO 27001:2022 Annexe A : 4 themes (Org A.5, People A.6, Physical A.7, Tech A.8)
//
// ----------------------------------------------------------------------------

export const MANUAL_GOVERNANCE_CONTROLS: GovernanceControlDefinition[] = [

  // ===========================================================================
  //  1. GOUVERNANCE & POLITIQUES (GOV-*) - 7 controles
  // ===========================================================================
  {
    id: 'GOV-001', family: 'governance', severity: 'critical',
    title: 'Politique de sécurité de l\'information approuvée',
    description: 'Une politique générale de sécurité de l\'information est documentée, approuvée par la direction et diffusée à tout le personnel.',
    rationale: 'Pierre angulaire de tout SMSI. Exigée explicitement par NIS2 et ISO 27001 — sans ce document, aucune autre mesure n\'est défendable juridiquement.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(a)', label: 'Politiques d\'analyse de risque et de sécurité' },
      { framework: 'ISO27001', ref: 'A.5.1',         label: 'Politiques de sécurité de l\'information' },
      { framework: 'CyFun',    ref: 'ID.GV-1',       label: 'Information security policy established', cyfunLevel: 'Basic' },
    ],
    remediation: 'Rédiger une politique signée par le CEO/comité de direction couvrant : périmètre, rôles, principes (confidentialité/intégrité/disponibilité), gestion du risque, conformité. Revue annuelle obligatoire.',
  },
  {
    id: 'GOV-002', family: 'governance', severity: 'high',
    title: 'Rôles et responsabilités sécurité définis',
    description: 'Un RSSI (ou équivalent) est nommé, avec autorité claire. Les responsabilités sécurité sont documentées dans les fiches de poste concernées.',
    rationale: 'NIS2 exige que les entités importantes/essentielles aient une gouvernance sécurité formalisée. ISO A.5.2 et A.6.2 imposent l\'attribution de responsabilités.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 20',     label: 'Gouvernance — responsabilité du management' },
      { framework: 'ISO27001', ref: 'A.5.2',       label: 'Rôles et responsabilités sécurité' },
      { framework: 'CyFun',    ref: 'ID.AM-6',     label: 'Cybersecurity roles established', cyfunLevel: 'Basic' },
    ],
    remediation: 'Nommer un RSSI (ou Compliance Officer pour PME) par décision écrite. Documenter dans la job description : autorité budgétaire, périmètre, reporting direction. Communiquer à tous.',
  },
  {
    id: 'GOV-003', family: 'governance', severity: 'critical',
    title: 'Analyse de risque documentée et à jour',
    description: 'Une analyse de risque cybersécurité formalisée existe, identifie les actifs critiques, les menaces et les mesures de traitement. Revue ≥ annuelle.',
    rationale: 'NIS2 Art. 21(2)(a) impose une approche basée sur le risque. ISO A.5.3 et CyFun ID.RA-* exigent une méthode formelle (ISO 27005, EBIOS RM, NIST RMF).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(a)', label: 'Analyse de risque et politiques de sécurité' },
      { framework: 'ISO27001', ref: 'A.5.3',         label: 'Séparation des tâches' },
      { framework: 'ISO27001', ref: 'Clause 6.1',    label: 'Actions face aux risques et opportunités' },
      { framework: 'CyFun',    ref: 'ID.RA-1',       label: 'Asset vulnerabilities identified', cyfunLevel: 'Important' },
    ],
    remediation: 'Adopter une méthode (ISO 27005, EBIOS RM). Documenter : périmètre, actifs, menaces, vraisemblance, impact, traitement (réduire/transférer/accepter). Revue annuelle + après changement majeur.',
  },
  {
    id: 'GOV-004', family: 'governance', severity: 'high',
    title: 'Plan d\'action sécurité (PAQS) approuvé',
    description: 'Un plan d\'action sécurité pluriannuel (3 ans typique) est validé par la direction, budgété et suivi avec des KPIs.',
    rationale: 'Sans plan d\'action concret, l\'analyse de risque reste théorique. NIS2 exige des mesures "appropriées et proportionnées" — leur planification doit être traçable.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(1)',  label: 'Mesures proportionnées au risque' },
      { framework: 'ISO27001', ref: 'Clause 6.2',  label: 'Objectifs de sécurité et plans' },
      { framework: 'CyFun',    ref: 'ID.GV-4',     label: 'Governance addresses risks', cyfunLevel: 'Important' },
    ],
    remediation: 'Élaborer un PAQS avec : actions par axe (technique, humain, organisationnel), budget, responsable, échéance, KPI. Présenter au CoDir trimestriellement.',
  },
  {
    id: 'GOV-005', family: 'governance', severity: 'medium',
    title: 'Revue de direction annuelle SMSI',
    description: 'La direction tient au moins une revue annuelle formelle du SMSI (résultats audits, incidents, indicateurs, suites à donner).',
    rationale: 'ISO Clause 9.3 obligatoire. NIS2 Art. 20 responsabilise la direction sur la cybersécurité — la revue formelle est sa preuve d\'implication.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 20',     label: 'Responsabilité du management' },
      { framework: 'ISO27001', ref: 'Clause 9.3',  label: 'Revue de direction' },
      { framework: 'CyFun',    ref: 'ID.GV-3',     label: 'Legal/regulatory requirements managed', cyfunLevel: 'Important' },
    ],
    remediation: 'Planifier 1 fois/an minimum. Ordre du jour standard : indicateurs sécurité, audit interne, incidents majeurs, ressources, opportunités d\'amélioration. PV signé.',
  },
  {
    id: 'GOV-006', family: 'governance', severity: 'high',
    title: 'Conformité légale et réglementaire suivie',
    description: 'Un registre des obligations légales/réglementaires applicables (RGPD, NIS2, secteur, etc.) est maintenu et revu ≥ annuellement.',
    rationale: 'NIS2 et ISO 27001 imposent une veille réglementaire active. Sans registre, impossible de démontrer la conformité au régulateur.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(4)',    label: 'Obligations sectorielles' },
      { framework: 'ISO27001', ref: 'A.5.31',        label: 'Obligations légales, réglementaires et contractuelles' },
      { framework: 'CyFun',    ref: 'ID.GV-3',       label: 'Legal/regulatory requirements managed', cyfunLevel: 'Basic' },
    ],
    remediation: 'Tenir un registre (tableur ou GRC) : référence légale, exigence, impact, mesure de conformité, responsable, échéance de revue.',
  },
  {
    id: 'GOV-007', family: 'governance', severity: 'medium',
    title: 'Audit interne annuel cybersécurité',
    description: 'Un audit interne du SMSI est mené ≥ 1 fois/an, indépendant des opérations IT, avec rapport et plan d\'action correctif.',
    rationale: 'ISO Clause 9.2 obligatoire. Permet d\'identifier les écarts avant un audit externe ou un incident.',
    references: [
      { framework: 'ISO27001', ref: 'Clause 9.2',    label: 'Audit interne' },
      { framework: 'CyFun',    ref: 'ID.GV-2',       label: 'Information security roles coordinated', cyfunLevel: 'Important' },
    ],
    remediation: 'Mandater un auditeur interne (ou externe pour PME). Périmètre : SMSI complet en 3 ans cycle. Rapport au CoDir + RSSI. Suivi des écarts.',
  },

  // ===========================================================================
  //  2. RESSOURCES HUMAINES (HR-*) - 5 controles
  // ===========================================================================
  {
    id: 'HR-001', family: 'hr', severity: 'high',
    title: 'Screening avant embauche pour postes sensibles',
    description: 'Une vérification d\'antécédents (extrait casier judiciaire, vérification diplômes, références) est conduite avant embauche pour les postes IT/sécurité/admin.',
    rationale: 'Insider threats représentent 20-30% des incidents. ISO A.6.1 obligatoire pour postes à risque. CyFun PR.IP-11 exigence Important.',
    references: [
      { framework: 'ISO27001', ref: 'A.6.1',     label: 'Vérification des antécédents' },
      { framework: 'CyFun',    ref: 'PR.IP-11',  label: 'HR cybersecurity included', cyfunLevel: 'Important' },
    ],
    remediation: 'Définir liste des postes "sensibles" (IT admin, finance, comptabilité, juridique). Procédure RH documentée avec checklist : casier judiciaire (≤ 3 mois), diplômes, références ≥ 2.',
  },
  {
    id: 'HR-002', family: 'hr', severity: 'critical',
    title: 'Clauses de confidentialité dans tous les contrats',
    description: 'Tous les contrats de travail (CDI, CDD, intérim, consultants, stagiaires) incluent une clause de confidentialité et de propriété intellectuelle.',
    rationale: 'Recours légal en cas de fuite d\'information. ISO A.6.2 obligatoire. RGPD impose des clauses spécifiques pour DPO/RH.',
    references: [
      { framework: 'ISO27001', ref: 'A.6.2',     label: 'Termes et conditions d\'emploi' },
      { framework: 'ISO27001', ref: 'A.6.6',     label: 'Accords de confidentialité' },
      { framework: 'CyFun',    ref: 'PR.IP-11',  label: 'HR cybersecurity included', cyfunLevel: 'Basic' },
    ],
    remediation: 'Faire valider par RH+ juridique un template contrat avec clause confidentialité (durée illimitée après départ pour info classifiée). Avenant pour anciens contrats sans clause.',
  },
  {
    id: 'HR-003', family: 'hr', severity: 'critical',
    title: 'Procédure de départ collaborateur (offboarding)',
    description: 'Une procédure documentée gère le départ : récupération matériel, révocation accès logique/physique, transfert connaissances, exit interview sécurité.',
    rationale: 'Comptes "fantômes" et accès oubliés sont vecteurs courants. NIS2 Art. 21(2)(i) sur les RH. ISO A.6.5 exigence.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(i)', label: 'Sécurité des RH' },
      { framework: 'ISO27001', ref: 'A.6.5',         label: 'Responsabilités après cessation d\'emploi' },
      { framework: 'CyFun',    ref: 'PR.AC-1',       label: 'Identities revoked', cyfunLevel: 'Basic' },
    ],
    remediation: 'Checklist offboarding (RH + IT) avec : désactivation comptes J-jour, transfert mailbox, récupération matériel/badges, NDA rappel, exit interview. Délai cible : J+1 ouvré.',
  },
  {
    id: 'HR-004', family: 'hr', severity: 'medium',
    title: 'Processus disciplinaire en cas de manquement',
    description: 'Un processus disciplinaire est documenté et communiqué, applicable en cas de violation des règles de sécurité.',
    rationale: 'Effet dissuasif. ISO A.6.4 obligatoire. Sans procédure, impossible de sanctionner légalement un manquement.',
    references: [
      { framework: 'ISO27001', ref: 'A.6.4',     label: 'Processus disciplinaire' },
      { framework: 'CyFun',    ref: 'PR.IP-11',  label: 'HR cybersecurity included', cyfunLevel: 'Important' },
    ],
    remediation: 'Documenter dans le règlement intérieur (validé CSE/comité d\'entreprise). Échelle proportionnée : rappel, avertissement, mise à pied, licenciement faute grave.',
  },
  {
    id: 'HR-005', family: 'hr', severity: 'high',
    title: 'Engagement de confidentialité signé annuellement',
    description: 'Tous les collaborateurs ayant accès à des informations sensibles signent un engagement de confidentialité ≥ 1 fois/an.',
    rationale: 'Sensibilise au fait que la confidentialité n\'est pas implicite. Réutilisable comme preuve en cas de litige.',
    references: [
      { framework: 'ISO27001', ref: 'A.6.6',     label: 'Accords de confidentialité' },
      { framework: 'CyFun',    ref: 'PR.IP-11',  label: 'HR cybersecurity included', cyfunLevel: 'Important' },
    ],
    remediation: 'Engagement de confidentialité distribué annuellement (campagne 1 mois). Signature électronique acceptable (DocuSign, Adobe Sign).',
  },

  // ===========================================================================
  //  3. SÉCURITÉ PHYSIQUE (PHY-*) - 4 controles
  // ===========================================================================
  {
    id: 'PHY-001', family: 'physical', severity: 'high',
    title: 'Contrôle d\'accès physique aux locaux IT',
    description: 'Les zones contenant des équipements IT critiques (datacenter, salle serveurs, baies) sont sécurisées par badge nominatif + journalisation.',
    rationale: 'Empêche accès physique non autorisé qui peut contourner toute mesure logique. ISO A.7.1-7.2 obligatoires.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Sécurité physique et environnementale' },
      { framework: 'ISO27001', ref: 'A.7.1',         label: 'Périmètres de sécurité physique' },
      { framework: 'ISO27001', ref: 'A.7.2',         label: 'Contrôles d\'entrée physiques' },
      { framework: 'CyFun',    ref: 'PR.AC-2',       label: 'Physical access managed', cyfunLevel: 'Basic' },
    ],
    remediation: 'Badge nominatif + logs centralisés (rétention ≥ 1 an). Revue trimestrielle des accès. Procédure visiteurs (registre + escorte).',
  },
  {
    id: 'PHY-002', family: 'physical', severity: 'medium',
    title: 'Protection contre les sinistres environnementaux',
    description: 'Les salles serveurs ont : détection incendie + extinction (gaz inerte recommandé), climatisation redondante, détection d\'eau, onduleur.',
    rationale: 'Continuité opérationnelle. ISO A.7.5/7.8 obligatoires. NIS2 Art. 21(2)(c).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Sécurité physique' },
      { framework: 'ISO27001', ref: 'A.7.5',         label: 'Protection contre menaces physiques' },
      { framework: 'ISO27001', ref: 'A.7.8',         label: 'Emplacement et protection équipements' },
      { framework: 'CyFun',    ref: 'PR.IP-5',       label: 'Physical environment protected', cyfunLevel: 'Important' },
    ],
    remediation: 'Audit installations : détection feu/eau, sprinklers ou FM-200, climatisation N+1, onduleur ≥ 30 min + groupe diesel. Test annuel.',
  },
  {
    id: 'PHY-003', family: 'physical', severity: 'medium',
    title: 'Politique "clean desk" et écran verrouillé',
    description: 'Une politique impose : bureau rangé en fin de journée (documents sensibles sous clé), verrouillage écran auto < 10 min.',
    rationale: 'Réduit risque de fuite par inattention ou vol opportuniste. ISO A.7.7 et A.8.1 (politique).',
    references: [
      { framework: 'ISO27001', ref: 'A.7.7',     label: 'Bureau dégagé et écran verrouillé' },
      { framework: 'CyFun',    ref: 'PR.AC-2',   label: 'Physical access managed', cyfunLevel: 'Important' },
    ],
    remediation: 'Politique signée + communication. GPO Windows : screen lock 10 min. Audits aléatoires (passer dans les bureaux après 18h).',
  },
  {
    id: 'PHY-004', family: 'physical', severity: 'medium',
    title: 'Destruction sécurisée des supports en fin de vie',
    description: 'Tout support (disque dur, SSD, papier, badge, smartphone) en fin de vie est détruit ou effacé de manière sécurisée (preuve traçable).',
    rationale: 'Empêche fuite d\'information depuis matériel mis au rebut. ISO A.7.10 et A.7.14 obligatoires.',
    references: [
      { framework: 'ISO27001', ref: 'A.7.10',    label: 'Supports de stockage' },
      { framework: 'ISO27001', ref: 'A.7.14',    label: 'Élimination sécurisée d\'équipements' },
      { framework: 'CyFun',    ref: 'PR.IP-6',   label: 'Data destroyed per policy', cyfunLevel: 'Important' },
    ],
    remediation: 'Contrat avec prestataire certifié (NF Z40-350 ou ISO 21964). Certificat de destruction archivé. Procédure interne pour matériel détruit en interne (effacement DBAN ou broyage).',
  },

  // ===========================================================================
  //  4. SÉCURITÉ RÉSEAU NON-M365 (NET-*) - 5 controles
  // ===========================================================================
  {
    id: 'NET-001', family: 'network', severity: 'high',
    title: 'Architecture réseau segmentée et documentée',
    description: 'Le réseau d\'entreprise est segmenté logiquement (VLAN, zones) : prod, dev, admin, invités, OT. Schéma à jour.',
    rationale: 'Limite la propagation latérale en cas de compromission. NIS2 Art. 21(2)(d) et CyFun PR.AC-5.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Sécurité de la chaîne d\'approvisionnement' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'ISO27001', ref: 'A.8.22',        label: 'Ségrégation des réseaux' },
      { framework: 'CyFun',    ref: 'PR.AC-5',       label: 'Network integrity protected', cyfunLevel: 'Important' },
    ],
    remediation: 'Schéma réseau (Visio/Lucidchart) avec VLANs, ACLs inter-zones, points d\'entrée Internet. Revue annuelle. Tester filtrage avec scan.',
  },
  {
    id: 'NET-002', family: 'network', severity: 'critical',
    title: 'Firewall périmétrique avec règles documentées',
    description: 'Un firewall de nouvelle génération (NGFW) protège l\'accès Internet. Les règles sont documentées, justifiées, revues ≥ 2 fois/an.',
    rationale: 'Première ligne de défense. ISO A.8.20 et A.8.23 obligatoires. NIS2 21(2)(g) hygiène cyber.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiène cyber' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'ISO27001', ref: 'A.8.23',        label: 'Filtrage Web' },
      { framework: 'CyFun',    ref: 'PR.PT-4',       label: 'Communications/control networks protected', cyfunLevel: 'Basic' },
    ],
    remediation: 'NGFW (Fortinet, Palo Alto, Sophos) avec : IPS, anti-malware, URL filtering, deny-all default. Documenter chaque règle ouverte (justification, owner, expiration).',
  },
  {
    id: 'NET-003', family: 'network', severity: 'high',
    title: 'VPN entreprise avec MFA pour accès distant',
    description: 'L\'accès distant au SI passe par un VPN ou ZTNA avec authentification multi-facteur obligatoire.',
    rationale: 'L\'accès distant est le vecteur principal d\'intrusion. NIS2 Art. 21(2)(j) MFA. CyFun PR.AC-3.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Authentification multi-facteur' },
      { framework: 'ISO27001', ref: 'A.5.14',        label: 'Transfert d\'information' },
      { framework: 'CyFun',    ref: 'PR.AC-3',       label: 'Remote access managed', cyfunLevel: 'Basic' },
    ],
    remediation: 'Solution VPN ou ZTNA (Cloudflare, Zscaler, Cisco). MFA obligatoire (Entra ID conditional access ou MFA natif). Pas d\'exception, même admins.',
  },
  {
    id: 'NET-004', family: 'network', severity: 'medium',
    title: 'Wi-Fi entreprise séparé du Wi-Fi invités',
    description: 'Le Wi-Fi corporate (WPA2/3 Enterprise, RADIUS, certif machine) est strictement séparé du Wi-Fi invités (captif, isolé Internet seul).',
    rationale: 'Empêche le rebond depuis le réseau invités vers le SI. ISO A.8.20 et A.8.22.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.20',    label: 'Sécurité des réseaux' },
      { framework: 'ISO27001', ref: 'A.8.22',    label: 'Ségrégation des réseaux' },
      { framework: 'CyFun',    ref: 'PR.AC-5',   label: 'Network integrity protected', cyfunLevel: 'Important' },
    ],
    remediation: '2 SSID : corporate (RADIUS + 802.1x) et guest (portail captif). VLAN distinct, pas de routage entre. Bandwidth limité pour guest.',
  },
  {
    id: 'NET-005', family: 'network', severity: 'medium',
    title: 'Détection d\'intrusion réseau (NIDS) ou SOC',
    description: 'Un système de détection d\'intrusion analyse le trafic (IDS/IPS, EDR, SIEM, SOC managé) avec investigation des alertes.',
    rationale: 'Sans détection, les attaques restent invisibles ≥ 200 jours en moyenne (rapport Mandiant). NIS2 Art. 21(2)(g).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiène cyber' },
      { framework: 'ISO27001', ref: 'A.8.16',        label: 'Activités de surveillance' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network monitored', cyfunLevel: 'Important' },
    ],
    remediation: 'NGFW avec IPS actif (au minimum), idéalement EDR (Defender for Endpoint) + SIEM (Sentinel). Pour PME : MSSP/SOC managé.',
  },

  // ===========================================================================
  //  5. CONTINUITÉ & SAUVEGARDES (BCP-*) - 5 controles
  // ===========================================================================
  {
    id: 'BCP-001', family: 'continuity', severity: 'critical',
    title: 'Plan de continuité d\'activité (PCA/BCP) documenté',
    description: 'Un PCA identifie les processus critiques, leur RTO/RPO, les ressources nécessaires, les scénarios de crise (cyber, pandémie, sinistre).',
    rationale: 'NIS2 Art. 21(2)(c) obligatoire pour entités essentielles/importantes. Sans BCP, impossible de garantir RTO/RPO au régulateur.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Gestion des incidents — continuité' },
      { framework: 'ISO27001', ref: 'A.5.29',        label: 'Sécurité de l\'information pendant interruption' },
      { framework: 'CyFun',    ref: 'RC.RP-1',       label: 'Recovery plan executed', cyfunLevel: 'Important' },
    ],
    remediation: 'BIA (Business Impact Analysis) pour identifier processus critiques + RTO/RPO. PCA documenté avec rôles, escalade, scénarios. Validation comité de crise.',
  },
  {
    id: 'BCP-002', family: 'continuity', severity: 'critical',
    title: 'Plan de reprise d\'activité (PRA/DR) testé',
    description: 'Un PRA décrit la restauration technique (failover, restore from backup) et est testé en conditions réelles ≥ 1 fois/an.',
    rationale: 'Un PRA non testé ≈ pas de PRA. NIS2 et ISO imposent les tests. Temps moyen de récupération sans test : x3 vs avec.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Gestion des incidents' },
      { framework: 'ISO27001', ref: 'A.5.30',        label: 'Préparation TIC pour continuité' },
      { framework: 'CyFun',    ref: 'PR.IP-9',       label: 'Response/recovery plans tested', cyfunLevel: 'Essential' },
    ],
    remediation: 'PRA détaillé (RTO par service, procédure failover, contacts). Test annuel : restore from backup réel, simulation de bascule. Compte-rendu + actions correctives.',
  },
  {
    id: 'BCP-003', family: 'continuity', severity: 'critical',
    title: 'Sauvegardes 3-2-1 avec rétention long terme',
    description: 'Politique de sauvegarde 3-2-1 : 3 copies, 2 médias différents, 1 hors site/cloud immuable. Rétention adaptée (quotidien 1 mois, hebdo 3 mois, mensuel 12 mois, annuel 7 ans).',
    rationale: 'Défense ultime contre ransomware. ISO A.8.13 obligatoire. CyFun PR.IP-4 Essential.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.13',    label: 'Sauvegarde de l\'information' },
      { framework: 'CyFun',    ref: 'PR.IP-4',   label: 'Backups maintained/tested', cyfunLevel: 'Essential' },
    ],
    remediation: 'Solution backup (Veeam, Rubrik, Cohesity). 1 copie air-gap ou immuable (object lock S3). Restauration test mensuel. Documentation rétention par type de donnée.',
  },
  {
    id: 'BCP-004', family: 'continuity', severity: 'high',
    title: 'Test de restauration sauvegarde mensuel',
    description: 'Un test de restauration d\'au moins 1 fichier/VM est effectué chaque mois et documenté.',
    rationale: 'Une sauvegarde non testée n\'existe pas. CyFun PR.IP-4 Essential. Beaucoup de victimes ransomware découvrent que leurs backups sont corrompus.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.13',    label: 'Sauvegarde de l\'information' },
      { framework: 'CyFun',    ref: 'PR.IP-4',   label: 'Backups tested', cyfunLevel: 'Essential' },
    ],
    remediation: 'Procédure test mensuel : restaurer 1 fichier aléatoire + 1 VM dans environnement isolé. Vérifier intégrité. Tracer dans un registre.',
  },
  {
    id: 'BCP-005', family: 'continuity', severity: 'medium',
    title: 'Cellule de crise constituée et joignable 24/7',
    description: 'Une cellule de crise cyber est constituée (RSSI, DSI, juridique, com, direction) avec contact joignable hors heures.',
    rationale: 'Les attaques majeures arrivent souvent vendredi soir / weekend. Sans cellule activable, perte de 24-48h critiques.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 23',     label: 'Obligations de notification' },
      { framework: 'ISO27001', ref: 'A.5.24',      label: 'Planification gestion incidents' },
      { framework: 'CyFun',    ref: 'RS.CO-2',     label: 'Incidents reported per criteria', cyfunLevel: 'Important' },
    ],
    remediation: 'Composition cellule documentée + suppléants. Annuaire H24 (téléphone perso pour membres clés). Test annuel d\'activation surprise.',
  },

  // ===========================================================================
  //  6. SUPPLY CHAIN & TIERS (SUP-*) - 4 controles
  // ===========================================================================
  {
    id: 'SUP-001', family: 'supply', severity: 'critical',
    title: 'Inventaire et évaluation cyber des fournisseurs critiques',
    description: 'Un inventaire des fournisseurs/sous-traitants critiques (cloud, MSP, infogérance, SaaS) est tenu avec évaluation de leur posture cyber.',
    rationale: 'NIS2 Art. 21(2)(d) explicite sur la chaîne d\'approvisionnement. Attaques supply chain en hausse (+78% sur 2024).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Sécurité de la chaîne d\'approvisionnement' },
      { framework: 'ISO27001', ref: 'A.5.19',        label: 'Sécurité dans les relations fournisseurs' },
      { framework: 'CyFun',    ref: 'ID.SC-1',       label: 'Supplier risk management', cyfunLevel: 'Important' },
    ],
    remediation: 'Registre fournisseurs critiques (criticité, données accédées, certification ISO 27001/SOC 2). Questionnaire annuel. Audit sur site pour TOP 5.',
  },
  {
    id: 'SUP-002', family: 'supply', severity: 'high',
    title: 'Clauses cyber dans les contrats fournisseurs',
    description: 'Les contrats fournisseurs accédant à des données ou systèmes incluent des clauses : confidentialité, droit d\'audit, notification incident, SLA, retour de données.',
    rationale: 'Recours juridique en cas d\'incident fournisseur. ISO A.5.20 et A.5.22 obligatoires.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Sécurité supply chain' },
      { framework: 'ISO27001', ref: 'A.5.20',        label: 'Aborder la sécurité dans contrats' },
      { framework: 'ISO27001', ref: 'A.5.22',        label: 'Suivi des services fournisseurs' },
      { framework: 'CyFun',    ref: 'ID.SC-3',       label: 'Contracts include cybersecurity', cyfunLevel: 'Important' },
    ],
    remediation: 'Annexe sécurité standard à valider par juridique. Inclure : ISO 27001 ou équivalent, droit d\'audit, notification incident < 24h, RPO/RTO.',
  },
  {
    id: 'SUP-003', family: 'supply', severity: 'medium',
    title: 'Revue annuelle des fournisseurs critiques',
    description: 'Une revue annuelle des fournisseurs critiques est conduite : posture cyber, conformité contractuelle, incidents survenus, alternatives.',
    rationale: 'La posture d\'un fournisseur évolue. ISO A.5.22 et CyFun ID.SC-4.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.22',    label: 'Suivi des services fournisseurs' },
      { framework: 'CyFun',    ref: 'ID.SC-4',   label: 'Suppliers monitored', cyfunLevel: 'Important' },
    ],
    remediation: 'Revue annuelle CoDir : top fournisseurs, certifications à jour, incidents survenus, plan B identifié. CR signé.',
  },
  {
    id: 'SUP-004', family: 'supply', severity: 'medium',
    title: 'Procédure de gestion d\'incident fournisseur',
    description: 'Une procédure documente comment réagir à une notification d\'incident venant d\'un fournisseur (impact analysis, escalade, communication).',
    rationale: 'Les notifications fournisseur sont fréquentes (data breach, vulnérabilité). Sans process, gestion chaotique.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Sécurité supply chain' },
      { framework: 'ISO27001', ref: 'A.5.21',        label: 'Sécurité dans accords TIC supply chain' },
      { framework: 'CyFun',    ref: 'RS.CO-4',       label: 'Coordination with stakeholders', cyfunLevel: 'Important' },
    ],
    remediation: 'Procédure : check criticité (données impactées ?), évaluation impact, escalade direction, communication interne, suivi remédiation fournisseur.',
  },

  // ===========================================================================
  //  7. FORMATION & SENSIBILISATION (TRN-*) - 4 controles
  // ===========================================================================
  {
    id: 'TRN-001', family: 'training', severity: 'high',
    title: 'Formation cybersécurité annuelle obligatoire',
    description: 'Tous les collaborateurs suivent une formation cybersécurité ≥ 1 fois/an (e-learning ou présentiel), avec traçabilité (taux ≥ 95%).',
    rationale: 'NIS2 Art. 21(2)(g) hygiène cyber. ISO A.6.3 obligatoire. Le facteur humain reste le vecteur n°1.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiène cyber' },
      { framework: 'ISO27001', ref: 'A.6.3',         label: 'Sensibilisation, éducation et formation' },
      { framework: 'CyFun',    ref: 'PR.AT-1',       label: 'Users trained', cyfunLevel: 'Basic' },
    ],
    remediation: 'Plateforme e-learning (Hoxhunt, KnowBe4, Riot, Mantra). Module obligatoire 30 min/an. Reporting RH : taux complétion par BU.',
  },
  {
    id: 'TRN-002', family: 'training', severity: 'high',
    title: 'Campagnes de phishing simulé régulières',
    description: 'Des campagnes de phishing simulé sont menées ≥ 4 fois/an, avec mesure du taux de clic et reformation des cliqueurs.',
    rationale: 'Test concret de la sensibilisation. NIS2 et CyFun PR.AT-1. Réduit taux clic de 20-30% à < 5% sur 12 mois.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hygiène cyber' },
      { framework: 'CyFun',    ref: 'PR.AT-1',       label: 'Users trained (incl. phishing)', cyfunLevel: 'Important' },
    ],
    remediation: 'Outil dédié (Microsoft Attack Simulation, KnowBe4, Phished). 4 campagnes/an avec scénarios variés. Reformation systématique des cliqueurs (module court < 5 min).',
  },
  {
    id: 'TRN-003', family: 'training', severity: 'medium',
    title: 'Formation spécifique pour le management',
    description: 'Les membres de la direction (CoDir, board) suivent une formation cybersécurité adaptée (gouvernance, responsabilité légale NIS2, gestion de crise) ≥ 1 fois/an.',
    rationale: 'NIS2 Art. 20 rend la direction personnellement responsable. ISO A.6.3 sensibilisation adaptée par rôle.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 20',     label: 'Responsabilité management — formation obligatoire' },
      { framework: 'ISO27001', ref: 'A.6.3',       label: 'Formation adaptée par rôle' },
      { framework: 'CyFun',    ref: 'PR.AT-3',     label: 'Senior executives understand role', cyfunLevel: 'Important' },
    ],
    remediation: 'Session executive 2h/an : enjeux NIS2, jurisprudence, cas concrets. Animée par RSSI ou consultant spécialisé. Présence obligatoire CoDir.',
  },
  {
    id: 'TRN-004', family: 'training', severity: 'medium',
    title: 'Formation technique avancée pour équipes IT',
    description: 'Les équipes IT et sécurité suivent des formations techniques avancées (CISSP, OSCP, certifications cloud) avec budget dédié.',
    rationale: 'Une équipe IT non formée applique des mauvaises pratiques. ISO A.6.3 adapté par rôle.',
    references: [
      { framework: 'ISO27001', ref: 'A.6.3',     label: 'Formation adaptée par rôle' },
      { framework: 'CyFun',    ref: 'PR.AT-2',   label: 'Privileged users trained', cyfunLevel: 'Important' },
    ],
    remediation: 'Plan formation annuel par membre IT/sécurité. Budget min 2000€/personne/an. Au moins 1 certification professionnelle / 2 ans.',
  },

  // ===========================================================================
  //  8. CRYPTOGRAPHIE (CRY-*) - 3 controles
  // ===========================================================================
  {
    id: 'CRY-001', family: 'crypto', severity: 'high',
    title: 'Politique cryptographique documentée',
    description: 'Une politique définit les algorithmes acceptés (AES-256, RSA-2048+, ECDSA P-256+, TLS 1.2+), interdits (DES, RC4, MD5, SHA-1), et les usages.',
    rationale: 'NIS2 Art. 21(2)(h) crypto. ISO A.8.24 obligatoire. Évite l\'utilisation d\'algorithmes obsolètes.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Politiques cryptographiques' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Utilisation de la cryptographie' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data at rest protected', cyfunLevel: 'Basic' },
    ],
    remediation: 'Politique alignée sur ANSSI/NIST/BSI. Liste algos OK et interdits. Validation par RSSI. Diffusion équipes IT/dev.',
  },
  {
    id: 'CRY-002', family: 'crypto', severity: 'high',
    title: 'Gestion centralisée des certificats',
    description: 'Les certificats TLS, code signing, S/MIME sont inventoriés (CMDB ou outil dédié) avec alertes d\'expiration ≥ 30 jours avant.',
    rationale: 'Certificat expiré = service down. ISO A.8.24 et CyFun PR.DS-2.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.24',    label: 'Cryptographie — gestion clés' },
      { framework: 'CyFun',    ref: 'PR.DS-2',   label: 'Data in transit protected', cyfunLevel: 'Important' },
    ],
    remediation: 'Outil dédié (KeyFactor, Venafi, AWS Certificate Manager). Inventaire complet avec dates, owners. Alertes 60/30/7 jours.',
  },
  {
    id: 'CRY-003', family: 'crypto', severity: 'medium',
    title: 'Gestion sécurisée des clés et secrets',
    description: 'Les secrets applicatifs (API keys, passwords, tokens) sont stockés dans un coffre-fort (Azure Key Vault, HashiCorp Vault), jamais en clair dans le code.',
    rationale: 'Fuites de secrets dans Git sont la principale source de compromission cloud. ISO A.8.24 et A.8.4.',
    references: [
      { framework: 'ISO27001', ref: 'A.8.4',     label: 'Accès au code source' },
      { framework: 'ISO27001', ref: 'A.8.24',    label: 'Gestion des clés' },
      { framework: 'CyFun',    ref: 'PR.DS-1',   label: 'Data at rest protected (secrets)', cyfunLevel: 'Important' },
    ],
    remediation: 'Adopter Azure Key Vault ou HashiCorp Vault. Scanner Git en CI (TruffleHog, GitGuardian). Rotation automatique des secrets.',
  },

  // ===========================================================================
  //  9. INVENTAIRE & CLASSIFICATION ACTIFS (AST-*) - 3 controles
  // ===========================================================================
  {
    id: 'AST-001', family: 'asset', severity: 'high',
    title: 'Inventaire des actifs informationnels',
    description: 'Un inventaire à jour liste : matériel (postes, serveurs, mobiles, IoT), logiciels, données, contrats SaaS, comptes d\'admin.',
    rationale: 'On ne peut sécuriser que ce qu\'on connaît. ISO A.5.9 obligatoire. CyFun ID.AM-1 Basic.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.9',     label: 'Inventaire des actifs' },
      { framework: 'CyFun',    ref: 'ID.AM-1',   label: 'Physical devices inventoried', cyfunLevel: 'Basic' },
      { framework: 'CyFun',    ref: 'ID.AM-2',   label: 'Software inventoried', cyfunLevel: 'Basic' },
    ],
    remediation: 'CMDB (ServiceNow, Lansweeper, BMC) ou tableur structuré. Champs : owner, criticité, classification, localisation. Inventaire alimenté automatiquement (agent).',
  },
  {
    id: 'AST-002', family: 'asset', severity: 'high',
    title: 'Classification des informations documentée',
    description: 'Un référentiel de classification (Public / Interne / Confidentiel / Strictement confidentiel) est documenté avec règles de manipulation par niveau.',
    rationale: 'Permet la protection adaptée. NIS2 Art. 21(2)(b). ISO A.5.12-5.13 obligatoires.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion d\'incidents — classification' },
      { framework: 'ISO27001', ref: 'A.5.12',        label: 'Classification de l\'information' },
      { framework: 'ISO27001', ref: 'A.5.13',        label: 'Étiquetage de l\'information' },
      { framework: 'CyFun',    ref: 'PR.IP-1',       label: 'Baseline configurations', cyfunLevel: 'Important' },
    ],
    remediation: 'Matrice 4 niveaux : critères de classification, règles de manipulation (stockage, transmission, destruction). Communiquer à tous + intégrer dans M365 Purview.',
  },
  {
    id: 'AST-003', family: 'asset', severity: 'medium',
    title: 'Propriétaire (owner) défini pour chaque actif critique',
    description: 'Chaque actif critique (application, base de données, processus) a un propriétaire métier identifié, responsable de sa sécurité.',
    rationale: 'Sans owner, personne ne décide. ISO A.5.9. Évite les "actifs orphelins".',
    references: [
      { framework: 'ISO27001', ref: 'A.5.9',     label: 'Inventaire et propriété des actifs' },
      { framework: 'CyFun',    ref: 'ID.AM-6',   label: 'Cybersecurity roles established', cyfunLevel: 'Important' },
    ],
    remediation: 'Pour chaque actif critique du CMDB : owner métier nommé. Validation annuelle. Owner = signataire des décisions sécurité concernant son actif.',
  },

  // ===========================================================================
  //  10. GESTION D'INCIDENT (INC-*) - 5 controles
  // ===========================================================================
  {
    id: 'INC-001', family: 'incident', severity: 'critical',
    title: 'Procédure de gestion d\'incident documentée',
    description: 'Une procédure documente : qualification, escalade, contention, éradication, recovery, retour d\'expérience. Diffusée aux équipes IT.',
    rationale: 'NIS2 Art. 21(2)(b) obligatoire. Sans procédure, gestion chaotique = perte de preuves + sur-impact.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion d\'incidents' },
      { framework: 'ISO27001', ref: 'A.5.24',        label: 'Planification gestion incidents' },
      { framework: 'ISO27001', ref: 'A.5.26',        label: 'Réponse aux incidents' },
      { framework: 'CyFun',    ref: 'RS.RP-1',       label: 'Response plan executed', cyfunLevel: 'Basic' },
    ],
    remediation: 'Procédure 1 page (matrice de criticité, contacts, étapes). Annexes par scénario : ransomware, phishing massif, fuite de données, compromission compte admin.',
  },
  {
    id: 'INC-002', family: 'incident', severity: 'critical',
    title: 'Notification incident NIS2 dans les 24h',
    description: 'Le processus garantit la notification d\'un incident significatif au CCB/régulateur dans les 24h (alerte précoce), 72h (notification d\'incident), 1 mois (rapport final).',
    rationale: 'NIS2 Art. 23 — délais légaux. Manquement = amende administrative (€10M ou 2% CA).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 23',     label: 'Obligations de notification' },
      { framework: 'CyFun',    ref: 'RS.CO-2',     label: 'Incidents reported', cyfunLevel: 'Important' },
    ],
    remediation: 'Procédure dédiée avec : critères de significativité, formulaires CCB pré-remplis, contacts juridique + RSSI + DPO, communication management.',
  },
  {
    id: 'INC-003', family: 'incident', severity: 'high',
    title: 'Tests de gestion de crise annuels',
    description: 'Un exercice de crise cyber (table-top minimum, idéalement red team) est conduit ≥ 1 fois/an pour tester la procédure et la cellule de crise.',
    rationale: 'Un plan jamais testé échouera. CyFun PR.IP-10 et RS.IM-2 importants.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Gestion incidents — exercices' },
      { framework: 'ISO27001', ref: 'A.5.27',        label: 'Apprentissage des incidents' },
      { framework: 'CyFun',    ref: 'PR.IP-10',      label: 'Response/recovery plans tested', cyfunLevel: 'Important' },
    ],
    remediation: 'Table-top exercise annuel (½ journée). Scénario réaliste. Participants : CoDir, IT, juridique, com. CR + plan d\'amélioration.',
  },
  {
    id: 'INC-004', family: 'incident', severity: 'medium',
    title: 'Retour d\'expérience (REX) systématique après incident',
    description: 'Tout incident significatif est suivi d\'un REX formel : timeline, causes racines, leçons apprises, actions correctives.',
    rationale: 'Apprentissage organisationnel. ISO A.5.27 obligatoire. Évite que l\'incident se reproduise.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.27',    label: 'Apprentissage des incidents' },
      { framework: 'CyFun',    ref: 'RS.IM-1',   label: 'Response plans incorporate lessons', cyfunLevel: 'Important' },
    ],
    remediation: 'REX systématique pour incidents Medium+ : analyse 5 Why ou Ishikawa. Plan d\'action SMART. Suivi mensuel des actions.',
  },
  {
    id: 'INC-005', family: 'incident', severity: 'medium',
    title: 'Communication de crise préparée',
    description: 'Des templates de communication de crise (interne, externe, clients, médias) sont préparés et validés (juridique + com).',
    rationale: 'En pleine crise, pas le temps de rédiger. Communication mauvaise = aggravation (perte de confiance, sanctions).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 23',     label: 'Notification — communication clients' },
      { framework: 'ISO27001', ref: 'A.5.6',       label: 'Contact avec autorités' },
      { framework: 'CyFun',    ref: 'RS.CO-1',     label: 'Personnel know their roles', cyfunLevel: 'Important' },
    ],
    remediation: 'Templates : message employés, message clients (RGPD), communiqué presse, FAQ. Validés juridique + com. Adaptable par scénario (ransomware, data breach).',
  },
];

/** Helpers pratiques */

export function getControlById(id: string): GovernanceControlDefinition | undefined {
  return MANUAL_GOVERNANCE_CONTROLS.find((c) => c.id === id);
}

export function getControlsByFamily(family: GovernanceFamily): GovernanceControlDefinition[] {
  return MANUAL_GOVERNANCE_CONTROLS.filter((c) => c.family === family);
}

/** Liste des familles ordonnees pour affichage. */
export const GOVERNANCE_FAMILY_ORDER: GovernanceFamily[] = [
  'governance', 'hr', 'physical', 'network', 'continuity',
  'supply', 'training', 'crypto', 'asset', 'incident',
];

/** Couleurs par famille gouvernance (theme distinct des familles M365). */
export const GOVERNANCE_FAMILY_COLORS: Record<GovernanceFamily, { fg: string; bg: string }> = {
  governance: { fg: '#1F3D5C', bg: '#E2EAF0' },   // bleu marine
  hr:         { fg: '#7A2B1F', bg: '#F8E2DC' },   // brique
  physical:   { fg: '#4A4A4A', bg: '#E5E5E5' },   // gris
  network:    { fg: '#1F5E6E', bg: '#DCEEF1' },   // teal
  continuity: { fg: '#5A2A86', bg: '#EBE0F8' },   // violet
  supply:     { fg: '#7A4B17', bg: '#FBE9D5' },   // orange
  training:   { fg: '#1F6E45', bg: '#DCF0E5' },   // vert
  crypto:     { fg: '#3B2A86', bg: '#E3DEF8' },   // indigo
  asset:      { fg: '#2D5F2D', bg: '#DCEFDC' },   // vert olive
  incident:   { fg: '#B82329', bg: '#FBE7E7' },   // rouge
};
